import React, { useState } from 'react';
import { ErrorBoundary, AvatarErrorBoundary } from './index';
import { useErrorHandler, useSupabaseErrorHandler, useStateSyncErrorHandler } from '@/hooks/useErrorHandler';
import { useNotification } from './NotificationCenter';

const ErrorSimulator: React.FC<{ title: string; onSimulateError: () => void }> = ({ title, onSimulateError }) => (
  <div className="p-4 border rounded-lg bg-red-50">
    <h3 className="font-semibold text-red-800 mb-2">{title}</h3>
    <button
      onClick={onSimulateError}
      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
    >
      Simular Erro
    </button>
  </div>
);

const AvatarErrorDemo: React.FC = () => {
  const [shouldFail, setShouldFail] = useState(false);

  if (shouldFail) {
    throw new Error('Falha ao carregar avatar: Image not found');
  }

  return (
    <div className="flex flex-col items-center gap-4 p-4 border rounded-lg bg-blue-50">
      <h3 className="font-semibold text-blue-800">Componente de Avatar</h3>
      <div className="w-20 h-20 bg-blue-200 rounded-full flex items-center justify-center">
        <span className="text-blue-600 text-sm">Avatar</span>
      </div>
      <button
        onClick={() => setShouldFail(true)}
        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
      >
        Falhar Avatar
      </button>
    </div>
  );
};

const HookErrorDemo: React.FC = () => {
  const { handleError, handleAsyncError, retryWithBackoff, getErrorStats } = useErrorHandler({
    context: 'HookDemo',
    severity: 'medium'
  });

  const { handleSupabaseError } = useSupabaseErrorHandler();
  const { handleStateSyncError } = useStateSyncErrorHandler();
  const { showNotification } = useNotification();

  const simulateBasicError = () => {
    const error = new Error('Erro básico simulado');
    handleError(error, 'Ação do usuário');
  };

  const simulateAsyncError = async () => {
    await handleAsyncError(async () => {
      throw new Error('Erro assíncrono simulado');
    }, 'Operação assíncrona');
  };

  const simulateSupabaseError = () => {
    const error = new Error('Supabase connection failed');
    error.stack = 'SupabaseError: Postgres connection timeout';
    handleSupabaseError(error, 'getUserProfile');
  };

  const simulateStateSyncError = () => {
    const error = new Error('State synchronization failed');
    handleStateSyncError(error, 'userProfile', async () => {
      // Simulate retry function
      console.log('Retrying state sync...');
    });
  };

  const simulateRetryWithBackoff = async () => {
    let attempts = 0;
    await retryWithBackoff(async () => {
      attempts++;
      if (attempts < 3) {
        throw new Error(`Attempt ${attempts} failed`);
      }
      return 'Success after retries!';
    }, 3, 500);
  };

  const showStats = () => {
    const stats = getErrorStats();
    showNotification({
      type: 'info',
      title: 'Estatísticas de Erro',
      message: `Total: ${stats.total}, Críticos: ${stats.bySeverity.critical}`,
      duration: 5000
    });
  };

  return (
    <div className="p-4 border rounded-lg bg-yellow-50">
      <h3 className="font-semibold text-yellow-800 mb-4">Demonstração de Hooks</h3>

      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={simulateBasicError}
          className="px-3 py-2 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700 transition-colors"
        >
          Erro Básico
        </button>

        <button
          onClick={simulateAsyncError}
          className="px-3 py-2 bg-orange-600 text-white rounded text-sm hover:bg-orange-700 transition-colors"
        >
          Erro Assíncrono
        </button>

        <button
          onClick={simulateSupabaseError}
          className="px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
        >
          Erro Supabase
        </button>

        <button
          onClick={simulateStateSyncError}
          className="px-3 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 transition-colors"
        >
          Erro State Sync
        </button>

        <button
          onClick={simulateRetryWithBackoff}
          className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors"
        >
          Retry c/ Backoff
        </button>

        <button
          onClick={showStats}
          className="px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
        >
          Mostrar Estatísticas
        </button>
      </div>
    </div>
  );
};

export const ErrorDemo: React.FC = () => {
  const [showGeneralError, setShowGeneralError] = useState(false);
  const [showCriticalError, setShowCriticalError] = useState(false);

  const simulateGeneralError = () => {
    setShowGeneralError(true);
    setTimeout(() => setShowGeneralError(false), 100);
  };

  const simulateCriticalError = () => {
    setShowCriticalError(true);
    setTimeout(() => setShowCriticalError(false), 100);
  };

  const simulateNetworkError = () => {
    throw new Error('NetworkError: Failed to fetch https://api.example.com/data');
  };

  const simulateReactError = () => {
    throw new ReferenceError('React is not defined');
  };

  const simulateTypeError = () => {
    const obj: any = null;
    return obj.property.method();
  };

  return (
    <div className="min-h-screen bg-card p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-8">
          Demonstração de Error Boundaries
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Avatar Error Boundary Demo */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">AvatarErrorBoundary</h2>
            <AvatarErrorBoundary
              context="AvatarDemo"
              enableAvatarRefresh={true}
              onAvatarError={(error) => {
                console.error('Avatar error handled:', error);
              }}
            >
              <AvatarErrorDemo />
            </AvatarErrorBoundary>
          </div>

          {/* Hooks Demo */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">Error Handler Hooks</h2>
            <HookErrorDemo />
          </div>

          {/* General Error Boundary Demo */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">ErrorBoundary General</h2>

            <ErrorBoundary
              context="GeneralDemo"
              severity="medium"
              enableRetry={true}
              maxRetries={3}
            >
              <div className="space-y-4">
                {showGeneralError && (
                  <ErrorSimulator
                    title="Erro General (Recuperável)"
                    onSimulateError={simulateGeneralError}
                  />
                )}

                <button
                  onClick={simulateGeneralError}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Disparar Erro General
                </button>

                <ErrorSimulator
                  title="Erro de Rede"
                  onSimulateError={simulateNetworkError}
                />

                <ErrorSimulator
                  title="TypeError"
                  onSimulateError={simulateTypeError}
                />
              </div>
            </ErrorBoundary>
          </div>

          {/* Critical Error Boundary Demo */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">ErrorBoundary Crítico</h2>

            <ErrorBoundary
              context="CriticalDemo"
              severity="critical"
              enableRetry={false}
            >
              <div className="space-y-4">
                {showCriticalError && (
                  <ErrorSimulator
                    title="Erro Crítico (Não Recuperável)"
                    onSimulateError={simulateCriticalError}
                  />
                )}

                <button
                  onClick={simulateCriticalError}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Disparar Erro Crítico
                </button>

                <ErrorSimulator
                  title="React Reference Error"
                  onSimulateError={simulateReactError}
                />
              </div>
            </ErrorBoundary>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-12 p-6 bg-card rounded-lg shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Instruções de Teste</h2>
          <ul className="space-y-2 text-foreground">
            <li>• <strong>AvatarErrorBoundary</strong>: Teste falhas no carregamento de avatar com opções de refresh</li>
            <li>• <strong>Error Handler Hooks</strong>: Experimente diferentes tipos de erros tratados pelos hooks</li>
            <li>• <strong>ErrorBoundary General</strong>: Teste erros recuperáveis com opção de retry</li>
            <li>• <strong>ErrorBoundary Crítico</strong>: Teste erros críticos sem opção de retry</li>
            <li>• Observe as notificações que aparecem para cada tipo de erro</li>
            <li>• Verifique o console para logs detalhados dos erros</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ErrorDemo;